import { useState } from "react";
import DataTable from "@components/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { useNavigate } from "react-router";
import { getFormattedDate } from "@utils/helperFunctions";
import {
  DIRECTION_COLOR,
  asPercent,
  biasSentence,
} from "@common/estimateAccuracy";
import useListServiceEstimateAccuracies from "../../api/useListServiceEstimateAccuracies";
import { ServiceEstimateAccuracy } from "../../types";
import "./style.css";

interface Props {
  serviceType: "professional_services" | "managed_services";
}

const ServiceReviewTable = ({ serviceType }: Props) => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const { accuracies, isLoading, pageMeta } = useListServiceEstimateAccuracies({
    pageNumber,
    pageSize,
    sortBy,
    sortOrder,
    // Only services with a finding to act on. A `none` row is a real result -
    // the account has the history to say there is no consistent bias - but it
    // is not something anyone can do anything about, so it stays out of the
    // list. Filtered server-side so paging and the row count describe what is
    // on screen.
    directions: ["over", "under"],
  });

  const onSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPageNumber(1);
  };

  const sortableHeader = (label: string, column: string) => (
    <div
      className="reviewColumnHeader sortable"
      onClick={() => onSort(column)}
      role="button"
    >
      {label}
    </div>
  );

  const columns = [
    {
      id: "service-name",
      header: () => <div className="reviewColumnHeader">Service</div>,
      cell(props) {
        const accuracy: ServiceEstimateAccuracy = props.row.original;
        if (!accuracy.serviceId) return accuracy.serviceName;
        return (
          <div
            className="reviewServiceName"
            role="button"
            onClick={() =>
              navigate(
                `/admin/${
                  serviceType === "managed_services"
                    ? "managed_services"
                    : "tasks"
                }/${accuracy.serviceId}/edit`
              )
            }
          >
            {accuracy.serviceName}
          </div>
        );
      },
    },
    {
      id: "bias-percentage",
      header: () => sortableHeader("Finding", "bias-percentage"),
      cell(props) {
        const accuracy: ServiceEstimateAccuracy = props.row.original;
        const color = DIRECTION_COLOR[accuracy.direction];
        return (
          <div className={accuracy.direction === "none" ? "noBias" : "finding"}>
            {color ? (
              <FontAwesomeIcon
                icon={faCircle}
                style={{ color, paddingRight: "8px" }}
              />
            ) : null}
            {biasSentence(accuracy.direction, accuracy.biasPercentage)}
          </div>
        );
      },
    },
    {
      id: "impact-hours",
      header: () => sortableHeader("Impact", "impact-hours"),
      cell(props) {
        const { impactHours }: ServiceEstimateAccuracy = props.row.original;
        // Blank, not "0h": the API zeroes impact on a no-bias row, so a zero
        // here means "not applicable", never "costs nothing".
        return impactHours === undefined ? (
          <span className="noBias">—</span>
        ) : (
          `${Math.round(impactHours)}h`
        );
      },
    },
    {
      id: "observation-count",
      header: () => sortableHeader("Deliveries", "observation-count"),
      cell(props) {
        const { observationCount }: ServiceEstimateAccuracy = props.row.original;
        return observationCount;
      },
    },
    {
      id: "confidence",
      header: () => sortableHeader("Confidence", "confidence"),
      cell(props) {
        const { confidence }: ServiceEstimateAccuracy = props.row.original;
        return confidence === undefined ? "" : asPercent(confidence);
      },
    },
    {
      id: "last-delivered-on",
      header: () => <div className="reviewColumnHeader">Last Delivered</div>,
      cell(props) {
        const { lastDeliveredOn }: ServiceEstimateAccuracy = props.row.original;
        return lastDeliveredOn
          ? getFormattedDate(new Date(lastDeliveredOn))
          : "";
      },
    },
  ];

  if (!isLoading && !accuracies.length) {
    return (
      <div className="reviewEmpty">
        No delivery data yet. Estimate accuracy is calculated weekly from
        services delivered through your PSA.
      </div>
    );
  }

  return (
    <DataTable
      data={accuracies}
      columns={columns}
      bordered
      striped
      hover
      classForTable="serviceReviewTable"
      totalPages={pageMeta.pageCount}
      totalRows={pageMeta.recordCount}
      currentPage={pageNumber}
      setCurrentPage={setPageNumber}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      paginationEnabled
      pageSizeEnabled
    />
  );
};

export default ServiceReviewTable;
